import { WordItem } from '../types';
import { DICTIONARY_BY_CATEGORY, ALL_UNIQUE_WORDS } from '../data/gameData';
import { getCustomWords, CustomWordItem } from './customVocabularyStorage';
import { normalizeCrosswordString } from './crosswordGenerator';
import { getFavoriteWords } from './favorites';

export const ALL_CHALLENGE_CATEGORIES: { id: string; name: string; icon: string }[] = [
  { id: 'Animaux', name: 'Animaux', icon: '🐾' },
  { id: 'La Maison', name: 'La Maison', icon: '🏠' },
  { id: 'Nature & Météo', name: 'Nature & Météo', icon: '🌿' },
  { id: 'Nombres & Quantité', name: 'Nombres & Quantité', icon: '🔢' },
  { id: 'Le Corps & Soins', name: 'Le Corps & Soins', icon: '🩺' },
  { id: 'Verbes', name: 'Verbes', icon: '🏃' },
  { id: 'Pensée & Communication', name: 'Pensée & Communication', icon: '🗣️' },
  { id: 'Verbes au Passé', name: 'Verbes au Passé', icon: '🕰️' },
  { id: 'Verbes au Futur & Présent', name: 'Verbes au Futur & Présent', icon: '⚡' },
  { id: 'Émotions & Sensations', name: 'Émotions & Sensations', icon: '💜' },
  { id: 'Histoires & École', name: 'Histoires & École', icon: '📚' },
  { id: 'Couleurs', name: 'Couleurs', icon: '🎨' },
  { id: 'Le Temps', name: 'Le Temps', icon: '⏳' },
  { id: 'Salutations & Politesse', name: 'Salutations & Politesse', icon: '👋' },
  { id: 'Pronoms & Personnes', name: 'Pronoms & Personnes', icon: '👤' },
  { id: 'Questions & Réponses', name: 'Questions & Réponses', icon: '❓' },
  { id: 'Phrases & Quotidien', name: 'Phrases & Quotidien', icon: '💬' },
  { id: 'La Famille', name: 'La Famille', icon: '👨‍👩‍👧' },
  { id: 'Nourriture', name: 'Nourriture', icon: '🍯' },
  { id: 'Jeux & Transports', name: 'Jeux & Transports', icon: '🚗' },
  { id: 'Lieux & Espace', name: 'Lieux & Espace', icon: '🗺️' },
  { id: 'Mots de liaison', name: 'Mots de liaison', icon: '🔗' },
  { id: 'Double Pronominalisation', name: 'Double Pronominalisation', icon: '🔀' },
  { id: 'Personnalisé', name: 'Personnalisé (Mes Mots)', icon: '📝' },
  { id: 'Favoris', name: 'Favoris (Mes Coups de Cœur)', icon: '❤️' },
];

export function getCategoryIcon(categoryName: string): string {
  const match = ALL_CHALLENGE_CATEGORIES.find(
    (c) => c.name.toLowerCase() === categoryName.toLowerCase() || c.id.toLowerCase() === categoryName.toLowerCase()
  );
  return match?.icon || '📌';
}

/**
 * Returns the complete, real-time list of words for a challenge category,
 * dynamically merging:
 * 1. Base dictionary words for this category
 * 2. User-created custom words assigned to this category
 * 3. Deduplication and override preservation
 */
export function getChallengeWords(category: string, playerId?: string): WordItem[] {
  const result: WordItem[] = [];
  const seenNormKab = new Set<string>();
  const seenNormFr = new Set<string>();

  // Special Category: Favoris (Words with Heart ❤️)
  if (category === 'Favoris') {
    let favList: string[] = [];
    try {
      favList = getFavoriteWords();
    } catch {
      favList = [];
    }
    const favSet = new Set(favList.map((f) => f.toLowerCase().trim()));

    // Search in ALL_UNIQUE_WORDS
    ALL_UNIQUE_WORDS.forEach((w) => {
      const origMatch = (w.originalKab || '').toLowerCase().trim();
      const currMatch = (w.kab || '').toLowerCase().trim();
      if (favSet.has(origMatch) || favSet.has(currMatch)) {
        const norm = normalizeCrosswordString(w.kab);
        if (!norm) return;
        const keyKab = norm.toLowerCase();
        const keyFr = (w.fr || '').toLowerCase().trim();
        if (!seenNormKab.has(keyKab) && !seenNormFr.has(keyFr)) {
          seenNormKab.add(keyKab);
          seenNormFr.add(keyFr);
          result.push(w);
        }
      }
    });

    // Also check player custom words
    let customWords: CustomWordItem[] = [];
    try {
      customWords = getCustomWords(playerId);
    } catch {}

    customWords.forEach((cw) => {
      const currMatch = (cw.kab || '').toLowerCase().trim();
      if (favSet.has(currMatch)) {
        const norm = normalizeCrosswordString(cw.kab);
        if (!norm) return;
        const keyKab = norm.toLowerCase();
        const keyFr = (cw.fr || '').toLowerCase().trim();
        if (!seenNormKab.has(keyKab) && !seenNormFr.has(keyFr)) {
          seenNormKab.add(keyKab);
          seenNormFr.add(keyFr);
          result.push({
            kab: cw.kab,
            fr: cw.fr,
            type: cw.type || 'Favoris',
            icon: cw.icon || '❤️',
          });
        }
      }
    });

    return result;
  }

  // 1. Fetch base dictionary words for this category
  const catNorm = category.trim().toLowerCase();
  let baseWords = DICTIONARY_BY_CATEGORY[category] || ALL_UNIQUE_WORDS.filter((w) => (w.type || '').trim().toLowerCase() === catNorm);
  if (baseWords.length === 0) {
    if (catNorm === 'actions & verbes' || catNorm === 'verbes') {
      baseWords = ALL_UNIQUE_WORDS.filter((w) =>
        w.type === 'Verbes' || w.type === "Verbes d'Action" || w.type === 'Verbes au Passé' || w.type === 'Verbes au Futur & Présent' || w.type === 'Pensée & Communication'
      );
    } else if (catNorm === "verbes d'action") {
      baseWords = DICTIONARY_BY_CATEGORY['Verbes'] || ALL_UNIQUE_WORDS.filter((w) => w.type === 'Verbes' || w.type === "Verbes d'Action");
    }
  }

  // 2. Fetch user custom words for this player
  let customWords: CustomWordItem[] = [];
  try {
    customWords = getCustomWords(playerId);
  } catch (e) {
    console.warn('Could not retrieve custom words:', e);
  }

  // Custom words that match this category
  const matchingCustom = customWords.filter((cw) => {
    if (category === 'Personnalisé') {
      return true; // The custom words challenge includes all user-authored words
    }
    return (cw.type || '').trim().toLowerCase() === category.trim().toLowerCase();
  });

  // Priority 1: Add custom user words (they can enrich or override dictionary entries)
  matchingCustom.forEach((cw) => {
    const norm = normalizeCrosswordString(cw.kab);
    if (!norm) return;
    const keyKab = norm.toLowerCase();
    const keyFr = (cw.fr || '').toLowerCase().trim();
    if (!seenNormKab.has(keyKab) && !seenNormFr.has(keyFr)) {
      seenNormKab.add(keyKab);
      seenNormFr.add(keyFr);
      result.push({
        kab: cw.kab,
        fr: cw.fr,
        type: cw.type || category,
        icon: cw.icon || getCategoryIcon(category),
      });
    }
  });

  // Priority 2: Add base category words
  baseWords.forEach((bw) => {
    const norm = normalizeCrosswordString(bw.kab);
    if (!norm) return;
    const keyKab = norm.toLowerCase();
    const keyFr = (bw.fr || '').toLowerCase().trim();
    if (!seenNormKab.has(keyKab) && !seenNormFr.has(keyFr)) {
      seenNormKab.add(keyKab);
      seenNormFr.add(keyFr);
      result.push(bw);
    }
  });

  return result;
}
